import { Controller } from "@hotwired/stimulus"

// TikTokトレンドの関連動画プレイヤー(テーブルラッパーに1つ付与する)
// - 「関連動画 N件」クリックで行直下に全幅パネル行(colspan)を差し込み、
//   テーブル左端から動画ストリップを表示する
// - パネル展開時に最初のeager本(デフォルト3)を自動読み込み、
//   残りは横スクロールで表示領域に入った時に遅延読み込み(クリックでも即時)
// - TikTok側CDNのレート制限を避けるため、読み込みはキューで1本ずつ間隔を空けて実行
// - ソート(th click)時はパネルを閉じる(行の並び替えでパネル行が迷子になるため)
export default class extends Controller {
  static values = { eager: { type: Number, default: 3 } }

  LOAD_INTERVAL_MS = 400

  connect() {
    this.queue = []
    this.draining = false
    this.openPanels = new Map()
    this.observers = []
    this.onHeaderClick = () => this.closeAll()
    this.sortHeaders = [...this.element.querySelectorAll("th[data-sort]")]
    // captureでtable_sortの並び替えより先にパネルを閉じる
    this.sortHeaders.forEach((th) => th.addEventListener("click", this.onHeaderClick, true))
  }

  disconnect() {
    this.sortHeaders?.forEach((th) => th.removeEventListener("click", this.onHeaderClick, true))
    this.observers.forEach((observer) => observer.disconnect())
  }

  toggle(event) {
    const row = event.currentTarget.closest("tr")
    const existing = this.openPanels.get(row)

    // 一度生成したパネルは削除せず再利用する(読み込み済みプレイヤーを再読み込みしない)
    if (existing) {
      if (existing.isConnected) {
        existing.classList.toggle("hidden")
      } else {
        // ソートで切り離されたパネルを親行の直下に戻す
        row.after(existing)
        existing.classList.remove("hidden")
      }
      return
    }

    const template = event.currentTarget.nextElementSibling
    if (!template || template.tagName !== "TEMPLATE") return

    const panelRow = document.createElement("tr")
    const cell = document.createElement("td")
    cell.colSpan = row.cells.length
    cell.className = "bg-base-200/40"
    cell.appendChild(template.content.cloneNode(true))
    panelRow.appendChild(cell)
    row.after(panelRow)
    // ストリップがセル幅を押し広げてテーブルが横に伸びないよう、
    // ラッパーの表示幅に固定してその枠内で横スクロールさせる
    const strip = panelRow.querySelector("[data-video-strip]")
    strip.style.maxWidth = `${this.element.clientWidth - 32}px`
    this.openPanels.set(row, panelRow)
    this.activate(panelRow)
  }

  // ソート時はパネル行が並び替えの対象にならないよう一旦DOMから切り離す
  // (参照は保持し、再度開いた時に親行の直下へ戻す)
  closeAll() {
    this.openPanels.forEach((panel) => panel.remove())
  }

  activate(panelRow) {
    const strip = panelRow.querySelector("[data-video-strip]")
    const buttons = [...strip.querySelectorAll("button[data-embed-url]")]
    buttons.slice(0, this.eagerValue).forEach((button) => this.enqueue(button))

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          observer.unobserve(entry.target)
          this.enqueue(entry.target)
        })
      },
      { root: strip, threshold: 0.6 }
    )
    buttons.slice(this.eagerValue).forEach((button) => observer.observe(button))
    this.observers.push(observer)
  }

  // プレースホルダーのクリック時は即時読み込み
  load(event) {
    this.replaceWithPlayer(event.currentTarget)
  }

  enqueue(button) {
    if (this.queue.includes(button)) return
    this.queue.push(button)
    this.drain()
  }

  async drain() {
    if (this.draining) return
    this.draining = true
    while (this.queue.length > 0) {
      const button = this.queue.shift()
      if (button.isConnected) this.replaceWithPlayer(button)
      await new Promise((resolve) => setTimeout(resolve, this.LOAD_INTERVAL_MS))
    }
    this.draining = false
  }

  replaceWithPlayer(button) {
    const url = button.dataset.embedUrl
    if (!url || !button.isConnected) return

    const iframe = document.createElement("iframe")
    iframe.src = url
    iframe.width = 176
    iframe.height = 320
    iframe.className = "rounded-lg border border-base-300"
    // referrerpolicy必須: TikTok側CDNがlocalhost等のリファラーをAccess Deniedにするため
    iframe.referrerPolicy = "no-referrer"
    iframe.allow = "fullscreen"
    button.replaceWith(iframe)
  }
}
